import createCandyRackClient from '../client';
import { logError } from '../../shared/utils/logging';
import { Product } from 'src/shared/types/Product';
import getProductsQuery from '../queries/getProductsQuery';
import getLanguage from '../../shared/utils/getLanguage';
import { REGISTERED_APPS } from 'src/shared/types/Apps';

const getIdFromGid = (gid?: string) => {
  if (!gid) {
    return 0;
  }

  return Number(gid.split('/').pop());
};

// Function that queries products data from Storefront API - or returns null
const getProducts = async (
  token: string,
  productIDs: number[],
  runningAppName: string,
): Promise<Product[] | null> => {
  const storefrontApiClient = createCandyRackClient(token);

  // Storefront client returns null if no token was provided
  if (!storefrontApiClient) {
    return null;
  }

  const query = getProductsQuery(
    productIDs,
    getLanguage()?.toUpperCase() ?? 'EN',
  );
  const {
    data,
    errors,
    // @ts-ignore - the types from the Shopify Storefront API client are not complete
  } = await storefrontApiClient.request(query);

  // In case of an error we return null
  if (errors) {
    logError(
      `Storefront API failed, using fallback, ${errors}`,
      runningAppName as REGISTERED_APPS,
    );
    return null;
  }

  // Check for empty response and use fallback
  if (!data) {
    return null;
  }

  return Object.entries(data)
    .filter(([key, value]) => value !== null && value !== undefined)
    .map(([key, value]) => {
      // Transform the product data to match the Product type
      const product = value as any;
      return {
        id: getIdFromGid(String(product.id)),
        title: product.title,
        handle: product.handle,
        available: product.availableForSale,
        description: product.description,
        short_description: product.description?.substring(0, 120) ?? '',
        tags: product.tags,
        featured_image: product.featuredImage?.url ?? '',
        variants: product.variants.edges.map(
          ({ node: variant }: { node: any }) => ({
            id: getIdFromGid(String(variant.id)),
            product_id: getIdFromGid(String(product.id)),
            title: variant.title,
            available: variant.availableForSale,
            price: parseFloat(variant.price.amount) * 100, // Convert to cents
            compare_at_price: variant.compareAtPrice
              ? parseFloat(variant.compareAtPrice.amount) * 100 // Convert to cents
              : 0,
            featured_image: variant.image
              ? { src: variant.image.url }
              : undefined,
          }),
        ),
      } as Product;
    });
};

export default getProducts;
